/**
 * The per-country aggregate endpoint (VERA-260) — what the map's CHOROPLETH mode paints.
 *
 * Follows `analyticsTopInfluencersApi` exactly, and for the same reasons: react-query
 * rather than the `store/analytics` saga machinery (this response is read by one card,
 * and its parameters include that card's own metric, so a shared redux slot would have to
 * be keyed per request anyway — the retrofit `AnalyticsScansMapCache` documents); types
 * declared against `@repo/types` rather than restated; arrays handed to axios rather than
 * hand-serialised. See that file's own notes — the `location=X` vs `location[]=X` finding
 * in particular is a live trap, not history.
 */
import type { ICountryMetrics } from '@repo/types'

import { createApiCall } from './index'

export interface CountryMetricsParams {
  /** ISO strings — the same wire form every analytics request uses. */
  from: string
  to: string
  /**
   * Denominator floor for a RATE metric. Omit to take the server's default (30). See
   * `ICountryMetrics` for why a rate choropleth needs one at all.
   */
  min_denominator?: number
  owner_ids?: string[]
  product_ids?: string[]
  certificate_ids?: string[]
  certificate_statuses?: string[]
  scan_statuses?: string[]
  location?: string[]
  /** `lat,lng;lat,lng;…` — the drawn ring, already encoded. */
  area?: string
}

/** See `analyticsTopInfluencersApi`'s note — axios' own serialiser emits `key[]=`, which
 *  is the form Express' `qs` parses as an array at every length. */
const toRequestParams = (params: CountryMetricsParams): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) =>
        value !== undefined && value !== null && (!Array.isArray(value) || value.length > 0),
    ),
  )

export const analyticsCountryMetricsApi = {
  get: async (params: CountryMetricsParams): Promise<ICountryMetrics> => {
    const { data } = await createApiCall({
      method: 'GET',
      url: '/analytics/admin/countries',
      params: toRequestParams(params),
    })
    return data.data
  },
}
